import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, UNAUTHORIZED_EVENT, useAction, useApi } from '../lib/api';
import { days, useBilling, useManageBilling, useSubscribe } from '../lib/billing';
import { SessionProvider, useSession } from '../lib/session';
import { sessionHint } from '../site/hooks';
import { LandingPage } from '../site/Landing';
import { pixelLoaded } from '../site/pixel';
import type { ApprovalRequest, BankTransaction, ChainVerification, Me, Org } from '../lib/types';
import { CIRCLE_EVENT, CircleToAsk, CTRL } from './CircleToAsk';
import { Icons } from './icons';
import { Avatar, Button, cx, Loading, Spinner } from './ui';

export function AppShell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // Re-checked on focus, so confirming the email in another tab unlocks this one when the person comes back.
  const me = useApi<Me>('/api/auth/me', { retry: false, staleTime: 60_000, refetchOnWindowFocus: true });
  // A sample company is built in the background after sign-up: check every few seconds until it's in.
  const org = useApi<Org>(me.data ? '/api/org' : null, { staleTime: 60_000, refetchInterval: (q) => (q.state.data?.sampleStatus === 'loading' ? 3000 : false) });

  useEffect(() => {
    const onUnauthorized = () => {
      qc.clear();
      navigate('/login', { replace: true });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [qc, navigate]);

  const signedOut = me.error instanceof ApiError && me.error.status === 401;
  useEffect(() => {
    if (me.data) sessionHint.set(true);
    else if (signedOut) sessionHint.set(false);
  }, [me.data, signedOut]);

  // Reddit's ad pixel (site/pixel.tsx) never runs in the app. Someone who signs in or up on a page that loaded it gets
  // a fresh page, which doesn't load it, as the browser is now marked signed in.
  const leavePixel = !!me.data && pixelLoaded();
  useEffect(() => {
    if (leavePixel) window.location.reload();
  }, [leavePixel]);

  // Visitors at `/` get the public landing page; anywhere else in the app, the sign-in page. A browser that wasn't
  // signed in last time sees the landing page while the session check runs, instead of a loading screen.
  const atHome = location.pathname === '/';
  if (signedOut) return atHome ? <LandingPage /> : <Navigate to="/login" replace />;
  if (atHome && me.isLoading && !sessionHint.get()) return <LandingPage />;
  if (leavePixel || me.isLoading || org.isLoading || !me.data || !org.data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading label="Opening your books…" />
      </div>
    );
  }
  if (org.data.sampleStatus) return <SampleBuilding org={org.data} />;
  return (
    <SessionProvider me={me.data} org={org.data}>
      <Shell />
    </SessionProvider>
  );
}

/** While a new sign-up's sample company is built (a few minutes), or if building it failed. */
function SampleBuilding({ org }: { org: Org }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const retry = useAction(() => api('/api/org/sample/retry', { method: 'POST' }));
  const failed = org.sampleStatus === 'failed';
  async function signOut() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    sessionHint.set(false);
    qc.clear();
    navigate('/login', { replace: true });
  }
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div aria-hidden className="app-backdrop" />
      <div role="status" className="relative w-full max-w-md rounded-2xl border border-line bg-surface p-8 text-center shadow-card">
        <Icons.Logo size={36} className="mx-auto" />
        <h1 className="mt-5 text-[19px] font-semibold tracking-[-0.01em]">{failed ? 'The sample company did not finish' : `Building ${org.name}`}</h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
          {failed
            ? 'Something went wrong while loading its history. Nothing else is affected: try again, and if it fails twice, write to us.'
            : '14 months of SaaS history: contracts, bills, bank feeds and two entities. This takes a few minutes. The books open by themselves when it is done, so you can close this tab and come back.'}
        </p>
        <div className="mt-6 flex justify-center">
          {failed ? (
            <Button variant="primary" loading={retry.isPending} onClick={() => retry.mutate()}>
              Try again
            </Button>
          ) : (
            <span className="flex items-center gap-2 text-[13px] text-muted">
              <Spinner /> Posting entries…
            </span>
          )}
        </div>
        <button onClick={() => void signOut()} className="mt-6 text-[12.5px] text-muted underline-offset-2 hover:underline">
          Sign out
        </button>
      </div>
    </div>
  );
}

function Shell() {
  // Below the lg breakpoint the sidebar is a drawer behind a menu button, so pages get the whole width of a phone.
  const [nav, setNav] = useState(false);
  const loc = useLocation();
  useEffect(() => setNav(false), [loc.pathname]);
  useEffect(() => {
    if (!nav) return;
    const h = (e: KeyboardEvent) => e.key === 'Escape' && setNav(false);
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [nav]);
  return (
    <div className="relative flex h-screen overflow-hidden print:block print:h-auto print:overflow-visible">
      <div aria-hidden className="app-backdrop" />
      {nav && <div aria-hidden className="animate-fade-in fixed inset-0 z-40 bg-ink/25 backdrop-blur-[2px] lg:hidden" onClick={() => setNav(false)} />}
      <Sidebar open={nav} onClose={() => setNav(false)} />
      <CircleToAsk />
      {/* Pages scroll under the floating top bar, which blurs them. */}
      <main className="relative min-h-0 min-w-0 flex-1 overflow-y-auto scrollbar-thin print:overflow-visible">
        <div className="sticky top-0 z-30 mx-auto max-w-[1440px] px-3 pt-3 sm:px-4 lg:px-6 print:hidden">
          <TopBar onMenu={() => setNav(true)} />
          <VerifyBanner />
          <BillingBanner />
        </div>
        <div className="mx-auto max-w-[1440px] px-4 pt-6 pb-10 sm:px-6 lg:px-8 print:p-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  count?: number;
  end?: boolean;
}

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const approvals = useApi<ApprovalRequest[]>('/api/approvals?status=PENDING', { refetchInterval: 60_000 });
  const unmatched = useApi<BankTransaction[]>('/api/bank-transactions?status=UNMATCHED&limit=500', { refetchInterval: 60_000 });
  const { me } = useSession();

  const groups: { title?: string; items: NavItem[] }[] = [
    {
      items: [
        { to: '/', label: 'Command center', icon: <Icons.Home />, end: true },
        { to: '/approvals', label: 'Approvals', icon: <Icons.Inbox />, count: approvals.data?.length },
        { to: '/assistant', label: 'Assistant', icon: <Icons.Sparkle /> },
      ],
    },
    {
      title: 'Ledger',
      items: [
        { to: '/journal', label: 'Journal entries', icon: <Icons.Book /> },
        { to: '/accounts', label: 'Chart of accounts', icon: <Icons.List /> },
        { to: '/close', label: 'Close', icon: <Icons.CheckCircle /> },
      ],
    },
    {
      title: 'Revenue',
      items: [
        { to: '/ar/customers', label: 'Customers', icon: <Icons.Users /> },
        { to: '/ar/products', label: 'Products', icon: <Icons.Box /> },
        { to: '/ar/contracts', label: 'Contracts', icon: <Icons.Doc /> },
        { to: '/ar/invoices', label: 'Invoices', icon: <Icons.Receipt /> },
      ],
    },
    {
      title: 'Spend',
      items: [
        { to: '/ap/vendors', label: 'Vendors', icon: <Icons.Building /> },
        { to: '/ap/bills', label: 'Bills', icon: <Icons.Bill /> },
      ],
    },
    {
      title: 'Cash',
      items: [{ to: '/cash', label: 'Bank reconciliation', icon: <Icons.Bank />, count: unmatched.data?.length }],
    },
    {
      title: 'Insights',
      items: [
        { to: '/dashboards', label: 'Dashboards', icon: <Icons.Dashboard /> },
        { to: '/reports', label: 'Reports', icon: <Icons.Chart /> },
        { to: '/controls', label: 'Controls', icon: <Icons.Shield /> },
      ],
    },
  ];

  const item = (isActive: boolean) =>
    cx(
      'group flex h-8 items-center gap-2.5 rounded-[10px] px-2 text-[13px] transition-colors',
      isActive ? 'bg-white/[0.14] text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.12)]' : 'text-white/75 hover:bg-white/[0.08] hover:text-white',
    );

  return (
    <aside
      aria-label="Main"
      className={cx(
        'glass-green z-50 flex w-[228px] shrink-0 flex-col overflow-hidden rounded-[22px] text-white print:hidden',
        'fixed inset-y-3 left-3 transition-transform duration-200 lg:relative lg:inset-auto lg:z-10 lg:my-3 lg:ml-3 lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-[calc(100%+24px)]',
      )}
    >
      <div className="flex h-14 items-center justify-between pr-2 pl-4">
        <Link to="/" className="flex items-center gap-2.5">
          <Icons.Logo className="rounded-[6px] ring-1 ring-white/15" />
          <span className="app-title text-[16px] text-white">RareMargin</span>
        </Link>
        <button onClick={onClose} className="rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white lg:hidden" aria-label="Close menu">
          <Icons.Close size={16} />
        </button>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-4 [scrollbar-width:none]">
        {groups.map((g, i) => (
          <div key={i} className={g.title ? 'mt-5' : 'mt-1'}>
            {g.title && <div className="app-eyebrow mb-1.5 px-2 text-[10px] text-white/45">{g.title}</div>}
            {g.items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-track={it.label} className={({ isActive }) => item(isActive)}>
                {({ isActive }) => (
                  <>
                    <span className={cx('transition-colors', isActive ? 'text-site-lime' : 'text-white/50 group-hover:text-site-lime-2')}>{it.icon}</span>
                    <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    {!!it.count && <span className="num rounded-full bg-site-lime px-1.5 text-[11px] font-semibold text-site-lime-ink">{it.count}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-2.5">
        {me.admin && (
          <NavLink to="/admin" className={({ isActive }) => item(isActive)}>
            {({ isActive }) => (
              <>
                <span className={isActive ? 'text-site-lime' : 'text-white/50 group-hover:text-site-lime-2'}>
                  <Icons.Chart />
                </span>
                Owner dashboard
              </>
            )}
          </NavLink>
        )}
        <NavLink to="/settings" data-track="Settings" className={({ isActive }) => item(isActive)}>
          {({ isActive }) => (
            <>
              <span className={isActive ? 'text-site-lime' : 'text-white/50 group-hover:text-site-lime-2'}>
                <Icons.Gear />
              </span>
              Settings
            </>
          )}
        </NavLink>
        <div className="mt-2 flex items-center gap-2 px-2 text-[12px] text-white/55">
          <span className="size-1.5 rounded-full bg-site-lime shadow-[0_0_8px_var(--color-site-lime)]" /> {me.role.toLowerCase()} · continuous close on
        </div>
      </div>
    </aside>
  );
}

const BANNER_TONE = { info: 'border-info/15 bg-info-soft text-info', warn: 'border-warn/20 bg-warn-soft text-warn', bad: 'border-bad/20 bg-bad-soft text-bad' };
const BANNER_BUTTON = { info: 'bg-info', warn: 'bg-warn', bad: 'bg-bad' };

/** Until the email is confirmed, the assistant and invites are locked: this says why and sends a new link. */
function VerifyBanner() {
  const { me } = useSession();
  const resend = useAction(() => api<{ alreadyVerified: boolean; email?: string }>('/api/auth/verify-email/resend', { method: 'POST' }), {
    success: (r) => (r.alreadyVerified ? 'Your email is already confirmed' : `Sent a new link to ${r.email}`),
  });
  if (me.user.emailVerified) return null;
  return (
    <div role="status" className={cx('mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-xl border px-4 py-2 text-[13px] shadow-card', BANNER_TONE.info)}>
      <span className="font-medium">
        Confirm your email to turn on the assistant and invite your team.
        <span className="font-normal"> We sent a link to {me.user.email}.</span>
      </span>
      <button
        onClick={() => resend.mutate()}
        disabled={resend.isPending}
        className={cx('inline-flex h-7 items-center rounded-md px-2.5 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-60', BANNER_BUTTON.info)}
      >
        {resend.isPending ? 'Sending…' : 'Send a new link'}
      </button>
    </div>
  );
}

/** Trial ending soon, a failed payment, or a read-only company: one line under the top bar, with the fix one click away. */
function BillingBanner() {
  const q = useBilling();
  const loc = useLocation();
  const subscribe = useSubscribe();
  const manage = useManageBilling();
  const b = q.data;
  if (!b?.enabled) return null;
  const a = b.access;
  const onBillingTab = loc.pathname === '/settings' && new URLSearchParams(loc.search).get('tab') === 'billing';

  let tone: keyof typeof BANNER_TONE;
  let text: string;
  let action: { label: string; run: () => void; pending: boolean } | null = null;
  if (a.state === 'trialing' && (a.trialDaysLeft ?? 99) <= 5) {
    tone = 'info';
    text = `Your free trial ends in ${days(a.trialDaysLeft!)}.`;
    action = { label: 'Subscribe', run: subscribe.start, pending: subscribe.pending };
  } else if (a.state === 'past_due') {
    tone = 'warn';
    text = "We couldn't take the last payment. Update your card to keep your subscription.";
    action = b.hasCustomer ? { label: 'Manage billing', run: manage.start, pending: manage.pending } : null;
  } else if (a.state === 'locked') {
    tone = 'bad';
    text = a.reason ?? 'This company is read-only until it subscribes.';
    action = { label: 'Subscribe', run: subscribe.start, pending: subscribe.pending };
  } else {
    return null;
  }

  return (
    <div role="status" className={cx('mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-xl border px-4 py-2 text-[13px] shadow-card', BANNER_TONE[tone])}>
      <span className="font-medium">
        {text}
        {!b.canManage && <span className="font-normal"> Ask an owner or admin to {a.state === 'past_due' ? 'update the card' : 'subscribe'}.</span>}
      </span>
      {b.canManage && !onBillingTab && (
        <span className="flex items-center gap-3">
          <Link to="/settings?tab=billing" className="text-[12.5px] underline-offset-2 hover:underline">
            Billing details
          </Link>
          {action && (
            <button
              onClick={action.run}
              disabled={action.pending}
              className={cx('inline-flex h-7 items-center rounded-md px-2.5 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-60', BANNER_BUTTON[tone])}
            >
              {action.pending ? 'Opening…' : action.label}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const { me, org } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loc = useLocation();
  const chain = useApi<ChainVerification>('/api/ledger/verify', { staleTime: 120_000 });
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMenu(false), [loc.pathname]);
  useEffect(() => {
    const h = (e: MouseEvent) => menuRef.current && !menuRef.current.contains(e.target as Node) && setMenu(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  async function signOut() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    sessionHint.set(false);
    qc.clear();
    navigate('/login', { replace: true });
  }

  async function switchOrg(orgId: string) {
    await api('/api/auth/switch-org', { body: { orgId } });
    qc.clear();
    navigate('/', { replace: true });
    window.location.reload();
  }

  return (
    <header className="glass-float flex h-14 items-center justify-between gap-3 rounded-2xl pr-2.5 pl-2.5 lg:gap-4 lg:pl-4">
      <div className="flex min-w-0 items-center gap-2 lg:gap-3">
        <button onClick={onMenu} className="rounded-[10px] p-1.5 text-ink-2 hover:bg-ink/[0.05] lg:hidden" aria-label="Open menu">
          <Icons.Menu size={18} />
        </button>
        {me.orgs.length > 1 ? (
          <select value={org.id} onChange={(e) => void switchOrg(e.target.value)} aria-label="Company" className="h-8 min-w-0 rounded-[9px] border border-line bg-surface px-2 text-[13px] font-medium">
            {me.orgs.map((o) => (
              <option key={o.orgId} value={o.orgId}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="app-title truncate text-[15px] text-ink">{org.name}</span>
        )}
        <span className="app-eyebrow hidden rounded-md bg-ink/[0.05] px-1.5 py-1 text-[10px] text-muted sm:inline">{org.baseCurrency}</span>
        {org.isSandbox && <span className="hidden rounded bg-warn-soft px-1.5 text-[11px] font-medium text-warn sm:inline">Sandbox</span>}
      </div>
      <div className="flex items-center gap-2">
        {chain.data && (
          <Link
            to="/controls"
            title={chain.data.ok ? `Hash chain verified across ${chain.data.checked.toLocaleString()} posted ${chain.data.checked === 1 ? 'entry' : 'entries'}` : 'Hash chain broken: see Controls'}
            className={cx('hidden h-8 items-center gap-1.5 rounded-[10px] px-2.5 text-[12px] font-medium md:inline-flex', chain.data.ok ? 'text-good hover:bg-good-soft' : 'bg-bad-soft text-bad')}
          >
            <Icons.Chain size={15} />
            {chain.data.ok ? <span className="num">Ledger sealed · {chain.data.checked.toLocaleString()} {chain.data.checked === 1 ? 'entry' : 'entries'}</span> : 'Ledger chain broken'}
          </Link>
        )}
        <button
          onClick={() => window.dispatchEvent(new Event(CIRCLE_EVENT))}
          data-track="Circle to ask"
          aria-label="Circle to ask"
          title={`Circle to ask: draw around anything to ask about it. Or hold ${CTRL} and draw.`}
          className="inline-flex size-8 items-center justify-center rounded-[10px] text-ink-2 hover:bg-ink/[0.05] hover:text-ink"
        >
          <Icons.Lasso size={17} />
        </button>
        <Link to="/assistant" className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-ai/20 bg-ai-soft/80 px-2.5 text-[12.5px] font-medium text-ai hover:border-ai/40 hover:bg-ai-soft">
          <Icons.Sparkle size={15} /> <span className="hidden sm:inline">Ask AI</span>
        </Link>
        <div ref={menuRef} className="relative">
          <button onClick={() => setMenu((m) => !m)} className="flex items-center gap-2 rounded-[10px] p-1 hover:bg-ink/[0.05]" aria-haspopup="menu" aria-expanded={menu} aria-label={`Account menu for ${me.user.name}`}>
            <Avatar name={me.user.name} />
          </button>
          {menu && (
            // Near-solid: the top bar already blurs the page, and a blur nested inside another one cannot see past it.
            <div role="menu" className="glass-solid animate-fade-up absolute right-0 z-30 mt-2 w-60 rounded-xl p-1">
              <div className="px-3 py-2">
                <div className="text-[13px] font-medium">{me.user.name}</div>
                <div className="text-[12px] text-muted">{me.user.email}</div>
                <div className="mt-1 text-[11.5px] text-muted">Role: {me.role.toLowerCase()}</div>
              </div>
              <div className="my-1 border-t border-ink/[0.07]" />
              <Link to="/settings?tab=account" className="flex h-8 items-center gap-2 rounded-lg px-3 text-[13px] hover:bg-ink/[0.05]">
                <Icons.Users size={15} /> Your account
              </Link>
              <Link to="/settings" className="flex h-8 items-center gap-2 rounded-lg px-3 text-[13px] hover:bg-ink/[0.05]">
                <Icons.Gear size={15} /> Settings
              </Link>
              <button onClick={() => void signOut()} className="flex h-8 w-full items-center gap-2 rounded-lg px-3 text-[13px] hover:bg-ink/[0.05]">
                <Icons.Logout size={15} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
